import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { Plus, Edit2, Trash2, Calendar, DollarSign, TrendingUp, Users, Eye, BarChart, Share2, Mail, MessageSquare, Clock, Zap, Settings, Target, Play, Pause } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertMarketingCampaignSchema, type MarketingCampaign, type InsertMarketingCampaign } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import EmailMarketing from "./EmailMarketing";
import SmsMarketing from "./SmsMarketing";
import WebToLeadForms from "./WebToLeadForms";
import Workflows from "./Workflows";

type MarketingSection = "email" | "sms" | "forms" | "workflows";

export default function Marketing() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<MarketingCampaign | null>(null);
  // NUQS: URL-based state management for top-level navigation (Email Marketing, SMS Marketing, etc.)
  const [activeTab, setActiveTab] = useQueryState(
    "section",
    parseAsStringLiteral(["email", "sms", "forms", "workflows"]).withDefault("email")
  );
  const [campaignBuilderOpen, setCampaignBuilderOpen] = useState(false);
  const [selectedCampaignType, setSelectedCampaignType] = useState<string>("");
  const [emailWorkflowOpen, setEmailWorkflowOpen] = useState(false);
  const [emailWorkflow, setEmailWorkflow] = useState({
    triggers: [],
    actions: [],
    conditions: []
  });
  const { hasFeature, isLoading: tierLoading } = useSubscriptionTier();
  const sectionFeatures: Record<MarketingSection, string> = {
    email: "email_campaigns",
    sms: "sms_messaging",
    forms: "marketing_basic",
    workflows: "marketing_intermediate",
  };
  const allowedSections = useMemo(
    () =>
      (["email", "sms", "forms", "workflows"] as MarketingSection[]).filter(
        (section) => hasFeature(sectionFeatures[section])
      ),
    [hasFeature]
  );
  const canManageCampaigns = hasFeature("email_campaigns");

  useEffect(() => {
    if (tierLoading || allowedSections.length === 0) return;
    if (!activeTab || !allowedSections.includes(activeTab as MarketingSection)) {
      setActiveTab(allowedSections[0]);
    }
  }, [tierLoading, allowedSections, activeTab, setActiveTab]);

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['/api/marketing-campaigns'],
    enabled: canManageCampaigns,
    queryFn: async () => {
      const response = await fetch('/api/marketing-campaigns');
      if (!response.ok) throw new Error('Failed to fetch campaigns');
      return response.json();
    }
  });

  const form = useForm<InsertMarketingCampaign>({
    resolver: zodResolver(insertMarketingCampaignSchema),
    defaultValues: {
      name: "",
      type: "",
      description: "",
      startDate: new Date(),
      endDate: null,
      budget: "",
      spent: "",
      leads: 0,
      conversions: 0,
      status: "active"
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertMarketingCampaign) => {
      const response = await fetch('/api/marketing-campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create campaign');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/marketing-campaigns'] });
      setIsDialogOpen(false);
      form.reset();
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<InsertMarketingCampaign> }) => {
      const response = await fetch(`/api/marketing-campaigns/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update campaign');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/marketing-campaigns'] });
      setIsDialogOpen(false);
      setEditingCampaign(null);
      form.reset();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/marketing-campaigns/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete campaign');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/marketing-campaigns'] });
    }
  });

  // Campaign type configurations
  const campaignTypes = [
    {
      type: "email",
      title: "Email Marketing",
      description: "Create automated email workflows with triggers, tags, and delays",
      icon: Mail,
      color: "bg-blue-500"
    },
    {
      type: "ppc",
      title: "Pay-Per-Click",
      description: "Google Ads and paid advertising campaigns",
      icon: Target,
      color: "bg-green-500"
    },
    {
      type: "content",
      title: "Content Marketing",
      description: "Blog posts, articles, and educational content",
      icon: MessageSquare,
      color: "bg-orange-500"
    },
    {
      type: "webinar",
      title: "Webinar",
      description: "Educational webinars and virtual events",
      icon: Users,
      color: "bg-indigo-500"
    },
    {
      type: "event",
      title: "Event Marketing",
      description: "Trade shows, conferences, and local events",
      icon: Calendar,
      color: "bg-pink-500"
    }
  ];

  const onSubmit = (data: InsertMarketingCampaign) => {
    if (editingCampaign) {
      updateMutation.mutate({ id: editingCampaign.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (campaign: MarketingCampaign) => {
    setEditingCampaign(campaign);
    form.reset({
      name: campaign.name,
      type: campaign.type,
      description: campaign.description || "",
      startDate: new Date(campaign.startDate),
      endDate: campaign.endDate ? new Date(campaign.endDate) : null,
      budget: campaign.budget || "",
      spent: campaign.spent || "",
      leads: campaign.leads || 0,
      conversions: campaign.conversions || 0,
      status: campaign.status
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this campaign?")) {
      deleteMutation.mutate(id);
    }
  };

  const formatCurrency = (amount: string | null) => {
    if (!amount) return "$0";
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: "default",
      paused: "secondary",
      completed: "outline"
    } as const;
    
    return (
      <Badge variant={variants[status as keyof typeof variants] || "outline"}>
        {status}
      </Badge>
    );
  };

  const calculateROI = (campaign: MarketingCampaign) => {
    const spent = parseFloat(campaign.spent || "0");
    const conversions = campaign.conversions || 0;
    const avgDeal = 5000; // Estimated average deal value
    const revenue = conversions * avgDeal;
    
    if (spent === 0) return "N/A";
    const roi = ((revenue - spent) / spent) * 100;
    return `${roi.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading campaigns...</div>
        </div>
      </div>
    );
  }

  if (tierLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading marketing access...</div>
        </div>
      </div>
    );
  }

  if (!hasFeature("marketing_basic")) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
        </div>
        <div className="rounded-lg border p-6 text-sm text-muted-foreground">
          Your current subscription tier does not include marketing features.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Marketing Campaigns</h1>
      </div>

      {canManageCampaigns && (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? "Edit Campaign" : "Create New Campaign"}
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Campaign Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter campaign name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Campaign Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select campaign type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="email">Email Marketing</SelectItem>
                            <SelectItem value="ppc">Pay-Per-Click</SelectItem>
                            <SelectItem value="content">Content Marketing</SelectItem>
                            <SelectItem value="webinar">Webinar</SelectItem>
                            <SelectItem value="event">Event Marketing</SelectItem>
                            <SelectItem value="referral">Referral Program</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} value={field.value || ""} placeholder="Campaign description" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : new Date())}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date (Optional)</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                              onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : null)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Budget</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} placeholder="0.00" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="spent"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Amount Spent</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} placeholder="0.00" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end space-x-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setIsDialogOpen(false);
                        setEditingCampaign(null);
                        form.reset();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={createMutation.isPending || updateMutation.isPending}
                    >
                      {editingCampaign ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </Form>
            </DialogContent>
      </Dialog>
      )}

      {/* Campaign Builder Dialog */}
      {canManageCampaigns && (
      <Dialog open={campaignBuilderOpen} onOpenChange={setCampaignBuilderOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Campaign Builder</DialogTitle>
          </DialogHeader>
          
          {!selectedCampaignType ? (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Choose Campaign Type</h3>
                <p className="text-muted-foreground">Select the type of campaign you want to create</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {campaignTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <Card 
                      key={type.type} 
                      className="cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => setSelectedCampaignType(type.type)}
                    >
                      <CardHeader>
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${type.color} text-white`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{type.title}</CardTitle>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : selectedCampaignType === "email" ? (
            <EmailWorkflowBuilder 
              onBack={() => setSelectedCampaignType("")}
              onSave={async (workflow) => {
                // Create campaign from email workflow
                const campaignData: InsertMarketingCampaign = {
                  name: workflow.name || "Email Campaign",
                  type: "email",
                  description: `Email automation workflow with ${workflow.trigger} trigger`,
                  startDate: new Date(),
                  endDate: null,
                  budget: null,
                  spent: null,
                  leads: 0,
                  conversions: 0,
                  status: "active"
                };
                
                await createMutation.mutateAsync(campaignData);
                setCampaignBuilderOpen(false);
                setSelectedCampaignType("");
              }}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Button variant="ghost" onClick={() => setSelectedCampaignType("")}>
                  ← Back
                </Button>
                <h3 className="text-lg font-semibold">
                  {campaignTypes.find(t => t.type === selectedCampaignType)?.title} Campaign
                </h3>
              </div>
              <div className="p-6 border border-dashed rounded-lg text-center">
                <p className="text-muted-foreground">
                  Advanced campaign builder for {selectedCampaignType} campaigns coming soon.
                </p>
                <Button 
                  className="mt-4"
                  onClick={() => {
                    setCampaignBuilderOpen(false);
                    setSelectedCampaignType("");
                    setIsDialogOpen(true);
                  }}
                >
                  Create Basic Campaign
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      )}



      {/* Campaigns List */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
        <TabsList className="bg-[#eff6fe] dark:bg-gray-800">
          {allowedSections.includes("email") && (
            <TabsTrigger value="email" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Email Marketing</TabsTrigger>
          )}
          {allowedSections.includes("sms") && (
            <TabsTrigger value="sms" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">SMS Marketing</TabsTrigger>
          )}
          {allowedSections.includes("forms") && (
            <TabsTrigger value="forms" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Web-to-Lead Forms</TabsTrigger>
          )}
          {allowedSections.includes("workflows") && (
            <TabsTrigger value="workflows" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Campaign Builder</TabsTrigger>
          )}
        </TabsList>


        {allowedSections.includes("email") && (
          <TabsContent value="email" className="space-y-4">
            <EmailMarketing />
          </TabsContent>
        )}

        {allowedSections.includes("sms") && (
          <TabsContent value="sms" className="space-y-4">
            <SmsMarketing />
          </TabsContent>
        )}

        {allowedSections.includes("forms") && (
          <TabsContent value="forms" className="space-y-4">
            <WebToLeadForms />
          </TabsContent>
        )}

        {allowedSections.includes("workflows") && (
          <TabsContent value="workflows" className="space-y-4">
            <Workflows />
          </TabsContent>
        )}
      </Tabs>

    </div>
  );
}


// Email Workflow Builder Component
function EmailWorkflowBuilder({ onBack, onSave }: { onBack: () => void; onSave: (workflow: any) => void }) {
  const [workflow, setWorkflow] = useState({
    name: "",
    trigger: "",
    actions: [],
    conditions: []
  });

  const triggers = [
    { id: "signup", name: "New Signup", description: "When someone signs up" },
    { id: "purchase", name: "Purchase Made", description: "When a purchase is completed" },
    { id: "abandoned_cart", name: "Abandoned Cart", description: "When cart is abandoned" },
    { id: "tag_added", name: "Tag Added", description: "When a specific tag is added" },
    { id: "date_based", name: "Date/Time", description: "Based on specific date or time" }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <h3 className="text-lg font-semibold">Email Workflow Builder</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Workflow Name</label>
          <Input 
            placeholder="Enter workflow name"
            value={workflow.name}
            onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Trigger</label>
          <Select onValueChange={(value) => setWorkflow(prev => ({ ...prev, trigger: value }))}>
            <SelectTrigger>
              <SelectValue placeholder="Select trigger" />
            </SelectTrigger>
            <SelectContent>
              {triggers.map(trigger => (
                <SelectItem key={trigger.id} value={trigger.id}>
                  <div>
                    <div className="font-medium">{trigger.name}</div>
                    <div className="text-xs text-muted-foreground">{trigger.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border border-dashed rounded-lg p-6 text-center">
          <Zap className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Drag and drop workflow actions here</p>
          <p className="text-xs text-muted-foreground mt-1">
            Add emails, delays, conditions, and tags to build your automation
          </p>
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onBack}>Cancel</Button>
          <Button onClick={() => onSave(workflow)}>Save Workflow</Button>
        </div>
      </div>

    </div>
  );
}

function SocialMediaConnectionForm({ platform, onConnect, onCancel }: {
  platform: string;
  onConnect: (credentials: any) => void;
  onCancel: () => void;
}) {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    apiKey: "",
    accessToken: "",
    appId: "",
    appSecret: "",
    pageId: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConnect(credentials);
  };

  const renderPlatformFields = () => {
    switch (platform.toLowerCase()) {
      case "facebook":
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="appId">App ID</Label>
              <Input
                id="appId"
                type="text"
                value={credentials.appId}
                onChange={(e) => setCredentials(prev => ({ ...prev, appId: e.target.value }))}
                placeholder="Enter Facebook App ID"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="appSecret">App Secret</Label>
              <Input
                id="appSecret"
                type="password"
                value={credentials.appSecret}
                onChange={(e) => setCredentials(prev => ({ ...prev, appSecret: e.target.value }))}
                placeholder="Enter Facebook App Secret"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="accessToken">Access Token</Label>
              <Input
                id="accessToken"
                type="password"
                value={credentials.accessToken}
                onChange={(e) => setCredentials(prev => ({ ...prev, accessToken: e.target.value }))}
                placeholder="Enter Facebook Access Token"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="pageId">Page ID (Optional)</Label>
              <Input
                id="pageId"
                type="text"
                value={credentials.pageId}
                onChange={(e) => setCredentials(prev => ({ ...prev, pageId: e.target.value }))}
                placeholder="Enter Facebook Page ID"
              />
            </div>
          </>
        );

      case "twitter":
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="apiKey">API Key</Label>
              <Input
                id="apiKey"
                type="password"
                value={credentials.apiKey}
                onChange={(e) => setCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="Enter Twitter API Key"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="appSecret">API Secret</Label>
              <Input
                id="appSecret"
                type="password"
                value={credentials.appSecret}
                onChange={(e) => setCredentials(prev => ({ ...prev, appSecret: e.target.value }))}
                placeholder="Enter Twitter API Secret"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="accessToken">Access Token</Label>
              <Input
                id="accessToken"
                type="password"
                value={credentials.accessToken}
                onChange={(e) => setCredentials(prev => ({ ...prev, accessToken: e.target.value }))}
                placeholder="Enter Twitter Access Token"
                required
              />
            </div>
          </>
        );

      case "linkedin":
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="username">LinkedIn Email</Label>
              <Input
                id="username"
                type="email"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter LinkedIn email"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter LinkedIn password"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="apiKey">Client ID (For API)</Label>
              <Input
                id="apiKey"
                type="text"
                value={credentials.apiKey}
                onChange={(e) => setCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="LinkedIn Client ID (optional)"
              />
            </div>
          </>
        );

      case "instagram":
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="username">Instagram Username</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter Instagram username"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter Instagram password"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="accessToken">Access Token (Business)</Label>
              <Input
                id="accessToken"
                type="password"
                value={credentials.accessToken}
                onChange={(e) => setCredentials(prev => ({ ...prev, accessToken: e.target.value }))}
                placeholder="Instagram Business Access Token (optional)"
              />
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="grid gap-2">
              <Label htmlFor="username">Username/Email</Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Enter username or email"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter password"
                required
              />
            </div>
          </>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {renderPlatformFields()}
      
      <div className="flex items-center justify-between pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Connect {platform}
        </Button>
      </div>
    </form>
  );
}
